import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, File as FileIcon, Check, Loader2, AlertTriangle, Share2 } from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import api from "@/fileshare/lib/api";
import { formatBytes, formatApiErrorDetail } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";

export default function FileRequestUpload() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/file-requests/${slug}`);
        setMeta(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Request not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const upload = async () => {
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/file-requests/${slug}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      setDone(true);
      toast.success("File uploaded successfully");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <PageWrap className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 text-blue-500 animate-spin" /></PageWrap>;

  if (error) {
    return (
      <PageWrap className="max-w-md mx-auto px-6 pt-24 text-center">
        <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4"><AlertTriangle className="h-6 w-6 text-red-500" /></div>
        <h1 className="text-xl font-semibold text-slate-900">{error}</h1>
      </PageWrap>
    );
  }

  return (
    <PageWrap className="max-w-lg mx-auto px-6 pt-20 pb-24">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
        className="bg-white border border-slate-200 rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.05)] p-8" data-testid="request-upload-card">
        <h1 className="text-xl font-semibold text-slate-900">{meta.title}</h1>
        {meta.description && <p className="text-sm text-slate-500 mt-2">{meta.description}</p>}

        {done ? (
          <div className="mt-8 text-center">
            <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3"><Check className="h-6 w-6 text-emerald-500" /></div>
            <p className="text-sm font-medium text-slate-900">Thanks! Your file was uploaded.</p>
            <Button data-testid="upload-more-btn" variant="outline" className="mt-5 border-slate-200" onClick={() => { setDone(false); setFile(null); setProgress(0); }}>Upload another</Button>
          </div>
        ) : (
          <>
            <div
              data-testid="request-upload-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              onClick={() => inputRef.current?.click()}
              className={`mt-6 cursor-pointer border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center transition-colors ${dragOver ? "border-blue-500 bg-blue-50/50" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"}`}
            >
              <input ref={inputRef} type="file" className="hidden" data-testid="request-file-input" onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
              {file ? (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center"><FileIcon className="h-5 w-5 text-blue-500" /></div>
                  <div className="text-left"><p className="text-sm font-medium text-slate-900">{file.name}</p><p className="text-xs text-slate-500">{formatBytes(file.size)}</p></div>
                </div>
              ) : (
                <><UploadCloud className="h-10 w-10 text-slate-400 mb-3" strokeWidth={1.5} /><p className="text-sm font-medium text-slate-700">Drop a file or click to browse</p></>
              )}
            </div>

            {uploading && (
              <div className="mt-5">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5"><span>Uploading…</span><span>{progress}%</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><motion.div className="h-full bg-blue-500" animate={{ width: `${progress}%` }} /></div>
              </div>
            )}

            <Button data-testid="request-upload-submit" onClick={upload} disabled={!file || uploading} className="mt-6 w-full bg-blue-500 hover:bg-blue-600 text-white h-11">
              {uploading ? "Uploading…" : "Upload file"}
            </Button>
          </>
        )}
      </motion.div>
      <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400"><Share2 className="h-4 w-4" /> Powered by FileShare</div>
    </PageWrap>
  );
}
